import { Bar, BarTooltipProps } from '@nivo/bar';
import { GavelIcon } from 'lucide-react';
import { memo, useMemo, useState } from 'react';
import DebouncedResizeContainer from '@/components/DebouncedResizeContainer';
import { useIsDarkMode } from '@/hooks/theme';
import { tsToLocaleDateString } from '@/lib/dateTime';
import { numberToLocaleString } from '@/lib/utils';
import { sanctionCategories, sanctionKinds, type SanctionKind } from '@/lib/sanctionCategories';
import type { SanctionStatsDayType } from '@shared/otherTypes';
import { StatsCardMessage, StatsLoadingSpinner } from './SanctionStatsGenericSubcards';
import { tooltipSurface } from '@/components/ui/tooltip';


type ChartDatum = {
    day: string;
    dayTs: number;
} & Record<SanctionKind, number>;

type SanctionsChartProps = {
    data: ChartDatum[];
    bucketDays: number;
    width: number;
    height: number;
};

const axisTextFill = 'rgba(247, 249, 251, .38)';

/**
 * Names the range a bucket covers - a day, a week or a month.
 */
const bucketLabel = (startTs: number, bucketDays: number) => {
    const start = tsToLocaleDateString(startTs, 'medium');
    if (bucketDays <= 1) return start;
    const endDate = new Date(startTs * 1000);
    endDate.setDate(endDate.getDate() + bucketDays - 1);
    const end = tsToLocaleDateString(Math.round(endDate.getTime() / 1000), 'medium');
    return `${start} - ${end}`;
};

const SanctionsChart = memo(({ data, bucketDays, width, height }: SanctionsChartProps) => {
    const isDarkMode = useIsDarkMode();

    //With big windows the axis would be unreadable, so only every nth day gets a tick
    const tickValues = useMemo(() => {
        const maxTicks = Math.max(2, Math.floor(width / 90));
        const step = Math.ceil(data.length / maxTicks);
        return data.filter((_, index) => index % step === 0).map((d) => d.day);
    }, [data, width]);

    const CustomTooltip = ({ id, value, indexValue, color }: BarTooltipProps<ChartDatum>) => {
        const datum = data.find((d) => d.day === indexValue);
        const total = datum
            ? sanctionKinds.reduce((acc, kind) => acc + datum[kind], 0)
            : 0;
        return (
            <div className={tooltipSurface}>
                <div className="pb-1 font-semibold text-fg">
                    {datum ? bucketLabel(datum.dayTs, bucketDays) : indexValue}
                </div>
                <div className="flex items-center gap-1.5">
                    <span className="size-2.5 rounded-full" style={{ backgroundColor: color }} />
                    {sanctionCategories[id as SanctionKind].label}: <strong>{numberToLocaleString(value)}</strong>
                </div>
                <div className="text-faint">
                    {bucketDays <= 1 ? 'Day total' : 'Period total'}: {numberToLocaleString(total)}
                </div>
            </div>
        );
    };

    if (!width || !height) return null;
    return (
        //the bars carry the tooltip, so they get the pointer cursor
        <div className="size-full [&_rect]:cursor-pointer">
            <Bar
                width={width}
                height={height}
                data={data}
                keys={[...sanctionKinds]}
                indexBy="day"
                theme={{
                    tooltip: { wrapper: { zIndex: 10000 } },
                    text: {
                        fontSize: '11px',
                        fontWeight: 600,
                        fill: axisTextFill,
                    },
                    axis: {
                        ticks: { text: { fill: axisTextFill } },
                    },
                    grid: {
                        line: {
                            strokeDasharray: '8 6',
                            stroke: 'rgba(247, 249, 251, .10)',
                            strokeOpacity: isDarkMode ? 1 : 0.25,
                            strokeWidth: 1,
                        },
                    },
                }}
                margin={{ top: 12, right: 16, bottom: 32, left: 44 }}
                padding={0.25}
                colors={({ id }) => sanctionCategories[id as SanctionKind].color}
                borderWidth={0}
                enableLabel={false}
                enableGridX={false}
                enableGridY={true}
                axisTop={null}
                axisRight={null}
                axisLeft={{
                    tickSize: 0,
                    tickPadding: 8,
                    format: (value) => Number.isInteger(value) ? numberToLocaleString(value as number) : '',
                }}
                axisBottom={{
                    tickSize: 0,
                    tickPadding: 8,
                    tickValues,
                }}
                tooltip={CustomTooltip}
                animate={false}
            />
        </div>
    );
});


type SanctionsTimelineCardProps = {
    isError?: boolean;
    series?: SanctionStatsDayType[];
    bucketDays?: number;
};

function SanctionsTimelineCard({ isError, series, bucketDays = 1 }: SanctionsTimelineCardProps) {
    const [chartSize, setChartSize] = useState({ width: 0, height: 0 });

    const chartData = useMemo(() => {
        if (!series) return;
        return series.map((day) => ({
            //short label so the axis stays readable, the tooltip carries the full date
            day: tsToLocaleDateString(day.dayTs, 'short'),
            dayTs: day.dayTs,
            warn: day.warn,
            analyse: day.analyse,
            jail: day.jail,
            anticheatJail: day.anticheatJail,
            ban: day.ban,
            anticheat: day.anticheat,
        })) satisfies ChartDatum[];
    }, [series]);

    const hasAnySanction = chartData?.some(
        (day) => sanctionKinds.some((kind) => day[kind] > 0)
    );

    return (
        <div className="md:rounded-hud border-edge bg-card shadow-surface flex flex-col">
            <div className="flex flex-row items-center justify-between px-1 sm:px-4 border-b rounded-t-[inherit] bg-secondary/35">
                <div className="flex items-center py-2 space-x-2">
                    <div className='hidden xs:block'><GavelIcon className="size-4" /></div>
                    <h2 className="font-mono text-sm">
                        {bucketDays <= 1
                            ? 'Sanctions per Day'
                            : `Sanctions per ${bucketDays} Days`}
                    </h2>
                </div>
                <div className="flex flex-wrap items-center justify-end gap-x-3 gap-y-1 py-2">
                    {sanctionKinds.map((kind) => (
                        <span key={kind} className="flex items-center gap-1.5 text-xs text-muted-foreground">
                            <span
                                className="size-2.5 rounded-full"
                                style={{ backgroundColor: sanctionCategories[kind].color }}
                            />
                            {sanctionCategories[kind].label}
                        </span>
                    ))}
                </div>
            </div>
            <div className="h-64 max-h-64">
                {chartData && hasAnySanction ? (
                    <DebouncedResizeContainer onDebouncedResize={setChartSize}>
                        <SanctionsChart
                            data={chartData}
                            bucketDays={bucketDays}
                            width={chartSize.width}
                            height={chartSize.height}
                        />
                    </DebouncedResizeContainer>
                ) : chartData ? (
                    <StatsCardMessage message="No sanctions in this period." />
                ) : (
                    <StatsLoadingSpinner isError={isError} />
                )}
            </div>
        </div>
    );
}

export default memo(SanctionsTimelineCard);
